class PickupScheduler extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.form = document.getElementById(this.formId);
    this.methodInputs = Array.from(this.querySelectorAll('[data-pickup-method]'));
    this.fieldsWrapper = this.querySelector('[data-pickup-fields-wrapper]');
    this.locationInput = this.querySelector('[data-pickup-location]');
    this.dateInput = this.querySelector('[data-pickup-date]');
    this.timeInput = this.querySelector('[data-pickup-time]');
    this.message = this.querySelector('[data-pickup-message]');
    this.selectionSummary = this.querySelector('[data-pickup-selection-summary]');
    this.selectionSummaryBody = this.querySelector('[data-pickup-selection-body]');
    this.checkoutSummary = this.querySelector('[data-pickup-checkout-summary]');
    this.checkoutSummaryBody = this.querySelector('[data-pickup-checkout-body]');
    this.summaryInput = this.querySelector('[data-pickup-summary-input]');
    this.clearLocationInput = this.querySelector('[data-pickup-clear-location]');
    this.clearDateInput = this.querySelector('[data-pickup-clear-date]');
    this.clearTimeInput = this.querySelector('[data-pickup-clear-time]');
    this.scheduleConfigNode = this.querySelector('[data-pickup-slot-config]');
    this.requiredMessage = this.dataset.requiredMessage || 'Please select a pickup location, date, and time.';
    this.cutoffMessage = this.dataset.cutoffMessage || 'Pickup ordering is closed for the selected day.';
    this.noSlotsMessage = this.dataset.noSlotsMessage || 'No pickup slots are currently available.';
    this.locationPlaceholder = this.dataset.locationPlaceholder || 'Select a pickup location';
    this.datePlaceholder = this.dataset.datePlaceholder || 'Select a pickup date';
    this.timePlaceholder = this.dataset.timePlaceholder || 'Select a pickup time';
    this.checkoutMatchMessage = this.dataset.checkoutMatchMessage || '';
    this.selectedMethod = (this.dataset.selectedMethod || 'pickup').toLowerCase() === 'delivery' ? 'delivery' : 'pickup';
    this.selectedLocation = this.dataset.selectedLocation || '';
    this.selectedDate = this.dataset.selectedDate || '';
    this.selectedTime = this.dataset.selectedTime || '';
    this.requireSelection = this.dataset.requireSelection === 'true';
    this.checkoutButtons = Array.from(document.querySelectorAll(`[data-pickup-checkout-for="${this.formId}"]`));
    this.schedules = this.getSchedules();
    this.generatedSlots = this.generateSlots();

    this.methodInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.selectedMethod = String(input.value || 'pickup').toLowerCase() === 'delivery' ? 'delivery' : 'pickup';
        this.syncState();
      });
    });

    if (this.locationInput) {
      this.locationInput.addEventListener('change', () => {
        this.selectedLocation = this.locationInput.value;
        this.selectedDate = '';
        this.selectedTime = '';
        this.renderDateOptions();
        this.renderTimeOptions();
        this.syncState();
      });
    }

    if (this.dateInput) {
      this.dateInput.addEventListener('change', () => {
        this.selectedDate = this.dateInput.value;
        this.selectedTime = '';
        this.renderTimeOptions();
        this.syncState();
      });
    }

    if (this.timeInput) {
      this.timeInput.addEventListener('change', () => {
        this.selectedTime = this.timeInput.value;
        this.syncState();
      });
    }

    if (this.form && !this.form.dataset.pickupSchedulerBound) {
      this.form.addEventListener('submit', (event) => {
        if (!this.isSubmissionAllowed()) {
          event.preventDefault();
          this.syncState(true);
        }
      });

      this.form.dataset.pickupSchedulerBound = 'true';
    }

    this.renderLocationOptions();
    this.renderDateOptions();
    this.renderTimeOptions();
    this.syncState();
  }

  isPickupSelected() {
    return this.selectedMethod !== 'delivery';
  }

  getSchedules() {
    if (!this.scheduleConfigNode) return [];

    try {
      const parsed = JSON.parse(this.scheduleConfigNode.textContent || '[]');

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((schedule, index) => {
          const location = String(schedule?.location || '').trim();
          const rules = Array.isArray(schedule?.rules)
            ? schedule.rules.map((rule) => ({
                weekday: Number.parseInt(rule?.weekday, 10),
                enabled: Boolean(rule?.enabled),
                start: String(rule?.start || '').trim(),
                end: String(rule?.end || '').trim(),
              }))
            : [];

          return {
            location,
            locationRank: Number.parseInt(schedule?.locationRank, 10) || index + 1,
            slotMinutes: Number.parseInt(schedule?.slotMinutes, 10) || 30,
            prepHours: Number.parseFloat(schedule?.prepHours) || 0,
            rules,
          };
        })
        .filter((schedule) => schedule.location);
    } catch (error) {
      console.error('PickupScheduler: invalid schedule configuration', error);
      return [];
    }
  }

  parseDateTime(value) {
    if (!value) return Number.NaN;

    const normalized = String(value).trim();
    const directValue = new Date(normalized).getTime();
    if (!Number.isNaN(directValue)) return directValue;

    return new Date(normalized.replace(' ', 'T')).getTime();
  }

  parseDateValue(value) {
    return new Date(`${value}T12:00:00`).getTime();
  }

  parseTimeLabel(label, date = '2000-01-01') {
    const normalized = String(label || '').trim();
    if (!normalized) return Number.NaN;

    const directValue = new Date(`${date} ${normalized}`).getTime();
    if (!Number.isNaN(directValue)) return directValue;

    return new Date(`${date}T${normalized}`).getTime();
  }

  parseTimeToMinutes(value) {
    const normalized = String(value || '').trim();
    const match = normalized.match(/^(\d{1,2}):(\d{2})$/);

    if (!match) return Number.NaN;

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return Number.NaN;
    return hours * 60 + minutes;
  }

  formatDateLabel(value) {
    const parsed = new Date(`${value}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;

    return parsed.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  formatTimeLabel(date) {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  sortLocations(locations) {
    return [...locations].sort((left, right) => {
      const leftRank = this.getLocationRank(left);
      const rightRank = this.getLocationRank(right);

      if (leftRank !== rightRank) return leftRank - rightRank;
      return left.localeCompare(right);
    });
  }

  sortDates(dates) {
    return [...dates].sort((left, right) => this.parseDateValue(left) - this.parseDateValue(right));
  }

  sortTimes(times, date) {
    return [...times].sort((left, right) => {
      const leftValue = this.parseTimeLabel(left, date);
      const rightValue = this.parseTimeLabel(right, date);

      if (Number.isNaN(leftValue) && Number.isNaN(rightValue)) return left.localeCompare(right);
      if (Number.isNaN(leftValue)) return 1;
      if (Number.isNaN(rightValue)) return -1;
      return leftValue - rightValue;
    });
  }

  getLocationRank(location) {
    const matchingSchedule = this.schedules.find((schedule) => schedule.location === location);
    return matchingSchedule ? Number(matchingSchedule.locationRank || 9999) : 9999;
  }

  toDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  addMinutes(date, minutes) {
    return new Date(date.getTime() + minutes * 60 * 1000);
  }

  createDateAtMinutes(baseDate, totalMinutes) {
    const nextDate = new Date(baseDate);
    nextDate.setHours(0, 0, 0, 0);
    nextDate.setMinutes(totalMinutes);
    return nextDate;
  }

  scheduleHasEnabledRules(schedule) {
    const rules = Array.isArray(schedule?.rules) ? schedule.rules : [];
    return rules.some((rule) => {
      const startMinutes = this.parseTimeToMinutes(rule?.start);
      const endMinutes = this.parseTimeToMinutes(rule?.end);
      return Boolean(rule?.enabled) && !Number.isNaN(startMinutes) && !Number.isNaN(endMinutes) && endMinutes > startMinutes;
    });
  }

  getScheduleByLocation(location) {
    return this.schedules.find((schedule) => schedule && schedule.location === location) || null;
  }

  getEligibleDatesForSchedule(schedule) {
    if (!schedule || !schedule.location) return [];

    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const prepHours = Number.parseFloat(schedule.prepHours || 0);
    const prepMs = (Number.isFinite(prepHours) ? prepHours : 0) * 60 * 60 * 1000;
    const threshold = now.getTime() + prepMs;
    const slotMinutes = Math.max(Number.parseInt(schedule.slotMinutes, 10) || 30, 1);
    const rules = Array.isArray(schedule.rules) ? schedule.rules : [];
    const dates = [];

    for (let offset = 0; offset <= 14; offset += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + offset);

      const weekday = date.getDay();
      const rule = rules.find((entry) => Number(entry?.weekday) === weekday && Boolean(entry?.enabled));

      if (!rule) continue;

      const startMinutes = this.parseTimeToMinutes(rule.start);
      const endMinutes = this.parseTimeToMinutes(rule.end);

      if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || endMinutes <= startMinutes) {
        continue;
      }

      let hasOpenSlot = false;

      for (let currentMinutes = startMinutes; currentMinutes + slotMinutes <= endMinutes; currentMinutes += slotMinutes) {
        const slotStart = this.createDateAtMinutes(date, currentMinutes);

        if (slotStart.getTime() >= threshold) {
          hasOpenSlot = true;
          break;
        }
      }

      if (hasOpenSlot) {
        dates.push(this.toDateKey(date));
      }
    }

    return this.sortDates([...new Set(dates)]);
  }

  generateSlots() {
    const schedules = this.schedules.filter((schedule) => schedule && schedule.location);
    if (!schedules.length) return [];

    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const generated = [];

    schedules.forEach((schedule) => {
      const prepHours = Number.parseFloat(schedule.prepHours || 0);
      const prepMs = (Number.isFinite(prepHours) ? prepHours : 0) * 60 * 60 * 1000;
      const threshold = now.getTime() + prepMs;
      const slotMinutes = Math.max(Number.parseInt(schedule.slotMinutes, 10) || 30, 1);
      const rules = Array.isArray(schedule.rules) ? schedule.rules : [];

      for (let offset = 0; offset <= 14; offset += 1) {
        const date = new Date(today);
        date.setDate(today.getDate() + offset);

        const weekday = date.getDay();
        const rule = rules.find((entry) => Number(entry.weekday) === weekday && Boolean(entry.enabled));

        if (!rule) continue;

        const startMinutes = this.parseTimeToMinutes(rule.start);
        const endMinutes = this.parseTimeToMinutes(rule.end);

        if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || endMinutes <= startMinutes) {
          continue;
        }

        for (let currentMinutes = startMinutes; currentMinutes + slotMinutes <= endMinutes; currentMinutes += slotMinutes) {
          const slotStart = this.createDateAtMinutes(date, currentMinutes);

          if (slotStart.getTime() < threshold) {
            continue;
          }

          generated.push({
            location: schedule.location,
            locationRank: Number(schedule.locationRank || 9999),
            date: this.toDateKey(date),
            time: this.formatTimeLabel(slotStart),
            timestamp: slotStart.getTime(),
          });
        }
      }
    });

    return generated.sort((left, right) => {
      if (left.locationRank !== right.locationRank) return left.locationRank - right.locationRank;
      if (left.timestamp !== right.timestamp) return left.timestamp - right.timestamp;
      return String(left.location).localeCompare(String(right.location));
    });
  }

  getOpenSlots() {
    return this.generatedSlots;
  }

  getLocations() {
    const configuredLocations = this.schedules
      .filter((schedule) => schedule && schedule.location && this.scheduleHasEnabledRules(schedule))
      .map((schedule) => schedule.location);

    return this.sortLocations([...new Set(configuredLocations)].filter(Boolean));
  }

  getDatesForLocation(location) {
    const schedule = this.getScheduleByLocation(location);
    const scheduleDates = this.getEligibleDatesForSchedule(schedule);

    if (scheduleDates.length) {
      return scheduleDates;
    }

    const dates = [
      ...new Set(
        this.getOpenSlots()
          .filter((slot) => slot.location === location)
          .map((slot) => slot.date)
      ),
    ];
    return this.sortDates(dates);
  }

  getTimesForSelection(location, date) {
    const uniqueTimes = [
      ...new Set(
        this.getOpenSlots()
          .filter((slot) => slot.location === location && slot.date === date)
          .map((slot) => slot.time)
      ),
    ];
    return this.sortTimes(uniqueTimes, date);
  }

  getSelectedSlotIsClosed() {
    if (!this.isPickupSelected() || !this.selectedLocation || !this.selectedDate) return false;

    const matchingSlots = this.getOpenSlots().filter(
      (slot) => slot.location === this.selectedLocation && slot.date === this.selectedDate
    );

    if (!matchingSlots.length) return false;

    return this.selectedTime
      ? !matchingSlots.some((slot) => slot.time === this.selectedTime)
      : false;
  }

  populateSelect(select, options, placeholder, selectedValue, formatter = null) {
    if (!select) return;

    const normalizedOptions = Array.isArray(options) ? options : [];
    const hasSelectedValue = normalizedOptions.includes(selectedValue);
    const nextSelectedValue = hasSelectedValue ? selectedValue : '';

    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    normalizedOptions.forEach((optionValue) => {
      const option = document.createElement('option');
      option.value = optionValue;
      option.textContent = formatter ? formatter(optionValue) : optionValue;

      if (optionValue === nextSelectedValue) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    select.value = nextSelectedValue;
  }

  renderLocationOptions() {
    const locations = this.getLocations();
    this.populateSelect(this.locationInput, locations, this.locationPlaceholder, this.selectedLocation);

    if (!locations.includes(this.selectedLocation)) {
      this.selectedLocation = '';
    }
  }

  renderDateOptions() {
    const dates = this.selectedLocation ? this.getDatesForLocation(this.selectedLocation) : [];
    this.populateSelect(this.dateInput, dates, this.datePlaceholder, this.selectedDate, (value) =>
      this.formatDateLabel(value)
    );

    if (!dates.includes(this.selectedDate)) {
      this.selectedDate = '';
    }
  }

  renderTimeOptions() {
    const times =
      this.selectedLocation && this.selectedDate
        ? this.getTimesForSelection(this.selectedLocation, this.selectedDate)
        : [];

    this.populateSelect(this.timeInput, times, this.timePlaceholder, this.selectedTime);

    if (!times.includes(this.selectedTime)) {
      this.selectedTime = '';
    }
  }

  getSelectionSummaryText() {
    if (!this.isPickupSelected()) return '';
    if (!(this.selectedLocation && this.selectedDate && this.selectedTime)) return '';
    return `${this.selectedLocation} — ${this.formatDateLabel(this.selectedDate)} at ${this.selectedTime}`;
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  renderSelectionSummary() {
    if (!this.selectionSummary || !this.selectionSummaryBody) return;

    if (!this.isPickupSelected()) {
      this.selectionSummary.hidden = true;
      this.selectionSummaryBody.innerHTML = '';
      return;
    }

    const summaryText = this.getSelectionSummaryText();

    if (!summaryText) {
      this.selectionSummary.hidden = true;
      this.selectionSummaryBody.innerHTML = '';
      return;
    }

    this.selectionSummary.hidden = false;
    this.selectionSummaryBody.innerHTML = `
      <p><strong>Location:</strong> ${this.escapeHtml(this.selectedLocation)}</p>
      <p><strong>Date:</strong> ${this.escapeHtml(this.formatDateLabel(this.selectedDate))}</p>
      <p><strong>Time:</strong> ${this.escapeHtml(this.selectedTime)}</p>
    `;
  }

  renderCheckoutSummary() {
    if (!this.checkoutSummary || !this.checkoutSummaryBody) return;

    if (!this.isPickupSelected()) {
      this.checkoutSummary.hidden = true;
      this.checkoutSummaryBody.innerHTML = '';
      return;
    }

    const summaryText = this.getSelectionSummaryText();

    if (!summaryText) {
      this.checkoutSummary.hidden = true;
      this.checkoutSummaryBody.innerHTML = '';
      return;
    }

    this.checkoutSummary.hidden = false;
    this.checkoutSummaryBody.innerHTML = `
      <p>You selected <strong>${this.escapeHtml(this.selectedLocation)}</strong>.</p>
      <p>Pickup is scheduled for <strong>${this.escapeHtml(this.formatDateLabel(this.selectedDate))}</strong> at <strong>${this.escapeHtml(this.selectedTime)}</strong>.</p>
    `;
  }

  syncSummaryAttribute() {
    if (!this.summaryInput) return;
    this.summaryInput.value = this.getSelectionSummaryText();
  }

  hasSelection() {
    if (!this.isPickupSelected()) return true;
    if (!this.requireSelection) return true;
    return Boolean(this.selectedLocation && this.selectedDate && this.selectedTime);
  }

  hasAvailableSlots() {
    if (!this.isPickupSelected()) return true;
    return this.getOpenSlots().length > 0;
  }

  isSubmissionAllowed() {
    return this.hasAvailableSlots() && !this.getSelectedSlotIsClosed() && this.hasSelection();
  }

  setCheckoutDisabled(disabled) {
    this.checkoutButtons.forEach((button) => {
      button.disabled = disabled;
      button.classList.toggle('button--disabled', disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  syncFieldVisibility() {
    const pickupSelected = this.isPickupSelected();

    if (this.fieldsWrapper) {
      this.fieldsWrapper.hidden = !pickupSelected;
    }

    [this.locationInput, this.dateInput, this.timeInput].forEach((input) => {
      if (input) {
        input.disabled = !pickupSelected;
      }
    });

    [this.clearLocationInput, this.clearDateInput, this.clearTimeInput].forEach((input) => {
      if (input) {
        input.disabled = pickupSelected;
      }
    });
  }

  syncState(showValidation = false) {
    this.generatedSlots = this.generateSlots();
    this.renderLocationOptions();
    this.renderDateOptions();
    this.renderTimeOptions();
    this.syncFieldVisibility();
    this.renderSelectionSummary();
    this.renderCheckoutSummary();
    this.syncSummaryAttribute();

    if (!this.isPickupSelected()) {
      this.setCheckoutDisabled(false);

      if (this.message) {
        this.message.hidden = true;
        this.message.textContent = '';
        this.message.dataset.state = '';
      }

      return;
    }

    if (!this.hasAvailableSlots()) {
      this.setCheckoutDisabled(true);

      if (this.message) {
        this.message.hidden = false;
        this.message.textContent = this.noSlotsMessage;
        this.message.dataset.state = 'closed';
      }

      return;
    }

    if (this.getSelectedSlotIsClosed()) {
      this.setCheckoutDisabled(true);

      if (this.message) {
        this.message.hidden = false;
        this.message.textContent = this.cutoffMessage;
        this.message.dataset.state = 'closed';
      }

      return;
    }

    if (!this.hasSelection()) {
      this.setCheckoutDisabled(true);

      if (this.message) {
        this.message.hidden = !showValidation;
        this.message.textContent = this.requiredMessage;
        this.message.dataset.state = 'warning';
      }

      return;
    }

    this.setCheckoutDisabled(false);

    if (this.message) {
      this.message.hidden = true;
      this.message.textContent = '';
      this.message.dataset.state = '';
    }
  }
}

customElements.define('pickup-scheduler', PickupScheduler);
